
import React, { useEffect } from 'react';
import { Exercise } from '@/models/LearnTypes';
import { cn } from '@/lib/utils';
import FlashCardExercise from './exercises/FlashCardExercise';
import SpellingExercise from './exercises/SpellingExercise';
import MatchExercise from './exercises/MatchExercise';
import CompletionTransition from './CompletionTransition';
import { useExerciseTransition } from '@/hooks/useExerciseTransition';
import { useExerciseProgress } from '@/hooks/exercise/useExerciseProgress';
import { useRecyclingPhase } from '@/hooks/exercise/useRecyclingPhase';

interface LevelExerciseProps {
  currentExercise: Exercise;
  showAnswer: boolean;
  userInput: string;
  isInputCorrect: boolean | null;
  onUserInputChange: (value: string) => void;
  onCheck: () => void;
  onNext: () => void;
  onPrev: () => void;
  isLastExercise: boolean;
  exerciseNumber?: number;
  totalExercises?: number;
  onClose?: () => void;
  exercises?: Exercise[];
  totalOriginalExercises?: number;
  wordsToReview?: string[];
  onComplete?: (wordsToReview: string[]) => void;
  onLogActivity?: (type: string, exercise: Exercise) => void;
  // Perfect level tracking
  isPerfectLevel?: boolean;
  onImperfectLevel?: () => void;
  // Added prop for direct word tracking
  onAddWordToReview?: (wordId: string) => void;
}

const LevelExercise = ({
  currentExercise,
  showAnswer,
  userInput,
  isInputCorrect,
  onUserInputChange,
  onCheck,
  onNext,
  onPrev,
  isLastExercise,
  exerciseNumber = 1,
  totalExercises = 1,
  onClose,
  exercises = [],
  totalOriginalExercises = 0,
  wordsToReview = [],
  onComplete = () => {},
  onLogActivity,
  // Perfect level tracking (default to true - levels start as perfect)
  isPerfectLevel = true,
  onImperfectLevel = () => {},
  // Direct word tracking handler
  onAddWordToReview = () => {}
}: LevelExerciseProps) => {
  const {
    completionTransition
  } = useExerciseTransition({
    currentExercise,
    isLastExercise,
    showAnswer,
    isInputCorrect,
    onNext
  });

  // Use the enhanced progress tracking
  const {
    progress,
    displayedExerciseNumber,
    isRecycledWord
  } = useExerciseProgress({
    exercises,
    currentExerciseIndex: exercises.findIndex(ex => ex.id === currentExercise.id),
    totalOriginalExercises,
    wordsToReview,
    currentExerciseId: currentExercise.id
  });
  
  // Check if we have words being tracked as difficult
  const { isInRecyclingPhase } = useRecyclingPhase({
    exercises,
    currentExerciseIndex: exercises.findIndex(ex => ex.id === currentExercise.id),
    totalOriginalExercises,
    wordsToReview,
    currentExerciseId: currentExercise.id
  });

  // Store the original exercises in window for tracking
  useEffect(() => {
    if (exercises.length > 0) {
      // Store in window for easy access
      (window as any).__ORIGINAL_EXERCISES__ = exercises;
    }
  }, [exercises]);

  const renderExerciseByType = () => {
    switch (currentExercise.type) {
      case 'match':
        return (
          <MatchExercise
            currentExercise={currentExercise}
            exercises={exercises}
            isAnimating={false}
            onNext={onNext}
            isLastExercise={isLastExercise}
            onComplete={onComplete}
            initialAnimation={false}
            onClose={onClose}
            // Perfect level tracking
            isPerfect={isPerfectLevel}
            onImperfect={onImperfectLevel}
            // Activity logging
            onLogActivity={onLogActivity}
          />
        );
      case 'flashcard':
        return (
          <FlashCardExercise
            currentExercise={currentExercise}
            isAnimating={false}
            onNext={onNext}
            onPrev={onPrev}
            isLastExercise={isLastExercise}
            nextCard={null}
            initialAnimation={false}
            exerciseNumber={displayedExerciseNumber}
            totalExercises={totalExercises}
            onClose={onClose}
            exercises={exercises}
            onComplete={onComplete}
            onUpdateProgress={(newProgress) => {
              // Update progress handling remains for UI consistency
              if (!isRecycledWord) {
                console.debug(`[FlashCard] Updating progress to ${newProgress}%`);
              } else {
                console.debug(`[FlashCard] Ignoring progress update for tracked failed word`);
              }
            }}
            mode="standard"
            onLogActivity={onLogActivity}
            // Perfect level tracking
            isPerfect={isPerfectLevel}
            onImperfect={onImperfectLevel}
            // Progress tracking
            isRecycledWord={isRecycledWord || isInRecyclingPhase}
            // Word tracking
            onAddWordToReview={onAddWordToReview}
          />
        );
      case 'reverse-flashcard':
        return (
          <FlashCardExercise
            currentExercise={currentExercise}
            isAnimating={false}
            onNext={onNext}
            onPrev={onPrev}
            isLastExercise={isLastExercise}
            nextCard={null}
            initialAnimation={false}
            exerciseNumber={displayedExerciseNumber}
            totalExercises={totalExercises}
            onClose={onClose}
            exercises={exercises}
            onComplete={onComplete}
            onUpdateProgress={(newProgress) => {
              // Update progress handling remains for UI consistency
              if (!isRecycledWord) {
                console.debug(`[ReverseFlashCard] Updating progress to ${newProgress}%`);
              } else {
                console.debug(`[ReverseFlashCard] Ignoring progress update for tracked failed word`);
              }
            }}
            mode="reverse"
            onLogActivity={onLogActivity}
            // Perfect level tracking
            isPerfect={isPerfectLevel}
            onImperfect={onImperfectLevel}
            // Progress tracking
            isRecycledWord={isRecycledWord || isInRecyclingPhase}
            // Word tracking
            onAddWordToReview={onAddWordToReview}
          />
        );
      case 'spelling':
        return (
          <SpellingExercise
            currentExercise={currentExercise}
            isAnimating={false}
            showAnswer={showAnswer}
            userInput={userInput}
            isInputCorrect={isInputCorrect}
            onUserInputChange={onUserInputChange}
            onCheck={onCheck}
            onNext={onNext}
            isLastExercise={isLastExercise}
            nextCard={null}
            initialAnimation={false}
            onClose={onClose}
            // Perfect level tracking
            isPerfect={isPerfectLevel}
            onImperfect={onImperfectLevel}
            // Progress tracking
            isRecycledWord={isRecycledWord || isInRecyclingPhase}
            // Activity logging
            onLogActivity={onLogActivity}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={cn(
      "mb-8 relative overflow-hidden",
      !completionTransition && "animate-fade-in"
    )}>
      {renderExerciseByType()}
      
      {completionTransition && <CompletionTransition />}
    </div>
  );
};

export default LevelExercise;
